import UTF8 from '@/filters/utf8'

// This component was intitially a port of Ivan Maksimovic's "fitr-svg-text" component. However, since it used SVG
// scaling, the positioning of the element was really difficult to work with, and so, a new version was created using
// SVG parameters.
//
// The resulting image is intended to work with ElectronPDF.
export default {
  name: 'fit-svg-text',

  data () {
    return {
      localWidth: 0,
      localX: 334,
      localY: 0,
      viewBox: '0, 0, 0, 0',
      scale: 1.0,
      initialFontSize: 0,
      displayFontSize: 0,
      loaded: false
    }
  },

  props: {
    // The class to set on the text node.
    textClassName: {
      type: String,
      default: ''
    },

    // The text to be displayed.
    text: {
      type: String,
      default: '',
      required: true
    },

    // From where to anchor the text field. One of "start", "middle", "end".
    textAnchor: {
      type: String,
      default: 'start'
    },

    // ViewBoxWidth is the width of the entire viewport of the SVG. Typically, the SVGs will all be of the same size,
    // so this value should not have to be set. However, some newer ones contain different values and they must
    // therefore be configurable.
    viewBoxWidth: {
      type: Number,
      default: 2266
    },

    // ViewBoxHeight is the height of the entire viewport of the SVG. Typically, the SVGs will all be of the same size,
    // so this value should not have to be set. However, some newer ones contain different values and they must
    // therefore be configurable.
    viewBoxHeight: {
      type: Number,
      default: 3132
    },

    // The max width of the final text field.
    maxWidth: 0,

    // The maximum height of the final text field.
    maxHeight: 0,

    // The parent height defines the base value from which we perform the calculations, making them the proper size in
    // the context of the whole SVG image.
    parentSVGHeight: 0,

    // The X position to place the outer container on. Basically, this is the root position that will be the base for
    // where the text will be calculated.
    containerX: 0,

    // The Y position to place the outer container on. Basically, this is the root position that will be the base for
    // where the text will be calculated.
    containerY: 0,

    // The X position to place the text viewbox on.
    x: 0,

    // The Y position to place the text viewbox on.
    y: 0,

    // A rotation to apply to the element, if it's not supposed to be straight.
    rotate: 0,

    // The component will compensate large strings by moving it up, attempting to center the content height-wise as
    // well. As a standard, it will move the text up (min-y) the same amount as the number of characters as the text is
    // long, which typically works well. However, in some cases, we want to move more or less.
    //
    // Set this value to anything else than 1.0 and the height compensation will be modified by the ratio set.
    heightModificationRatio: {
      type: Number,
      default: 1.0
    },

    // If we are writing the text along a path element, provide the ID of that path as this value.
    followPath: {
      type: String,
      default: ''
    }
  },

  computed: {
    textClean () {
      // Before we've loaded the text, we have to obtain some size values. For that reason, keep a short text that won't
      // require scaling before we'll start rendering.
      //
      // Loading is handled in the beforeMount hook.
      return !this.loaded ? 'Bob' : this.text.trim()
    },

    transform () {
      let ret = 'scale(' + this.scale + ')'
      let r = parseInt(this.rotate)
      if (!isNaN(r) && r !== 0) {
        ret += ' rotate(' + r + 'deg)'
      }
      return ret
    },

    transformNoDeg () {
      let ret = 'scale(' + this.scale + ')'
      let r = parseInt(this.rotate)
      if (!isNaN(r) && r !== 0) {
        ret += ' rotate(' + r + ')'
      }
      return ret
    },

    // If we're following a path, we need to provide a start offset on the text path element, or the text will be
    // clipped for non-left-aligned textAnchors.
    startOffset () {
      switch (this.textAnchor.toLowerCase()) {
        case 'middle':
          return '50%'
        case 'end':
          return '100%'
        default:
          return '0'
      }
    },

    // We can't generate PDFs from the poster if it contains non-latin characters. For that reason, we'll always output
    // the text as its unicode representation.
    out () {
      return UTF8.codePoints(this.textClean)
    }
  },

  mounted () {
    this.initialFontSize = this.obtainFontSize()
    this.loaded = true
    this.reRender()

    // @todo Fix! See description below.
    //
    // This is a hotfix to solve an issue where, if the text is really long, it will wrap around the path, which means
    // the text will be distorted without making the text field wider (the characters will be rendered in the same
    // place), which breaks with width calculation. For that reason, we'll re-render the text a few times to make sure
    // it's properly sized. We will only do this if we actually follow a path and have a long string.
    if (this.followPath !== '' && this.text.length > 9) {
      for (let i = 0; i < 100; i++) {
        this.reRender()
      }
    }
  },

  methods: {
    // Another attempt based on a StackOverflow question.
    //
    // @see https://stackoverflow.com/a/22580176/2670926
    fit () {
      // Obtain the text element.
      let textEl = this.$el.querySelector('text')
      if (!textEl) {
        console.log('no text element')
        return
      }

      let bbox = textEl.getBBox()
      if (!bbox) {
        console.log('no bbox')
        return
      }

      // If we're following a path, use the textPath's values instead.
      let pathEl = textEl.querySelector('textPath')
      if (pathEl) {
        try {
          bbox = pathEl.getBBox()
        } catch (err) {
          console.log(err)
        }
      }

      // Get values from the SVG bbox, forcing at least 1 to avoid division by zero.
      let bboxWidth = 1
      if (bbox.width) {
        bboxWidth = bbox.width
      }

      let bboxHeight = 1
      if (bbox.height) {
        bboxHeight = bbox.height
      }

      // Find out which ratio we should make the box to fit its limits.
      let bboxWidthRatio = this.maxWidth / bboxWidth
      let bboxHeightRatio = this.maxHeight / bboxHeight

      // If they're both smaller than their allowed one, use the one farthes from its allowed values.
      let ratio = 1.0
      switch (true) {
        // If we need to increase the size, use the ratio of the one we'd have to increase the least, so the other one
        // doesn't extend beyond.
        case bboxWidthRatio > 1.0 && bboxHeightRatio > 1.0:
          // ratio = Math.max(bboxWidthRatio, bboxHeightRatio)
          ratio = Math.min(bboxWidthRatio, bboxHeightRatio)
          break

        // If we need to make the text smaller, use the one we'd have to make the smaller by the most, so the other one
        // doesn't extend beyond.
        case bboxWidthRatio < 1.0 && bboxHeightRatio < 1.0:
          ratio = Math.min(bboxWidthRatio, bboxHeightRatio)
          break

        // If we'd have to make the text less wide, but the height is okay, use the width.
        case bboxWidthRatio <= 1.0 && bboxHeight > 1.0:
          ratio = bboxWidthRatio
          break

        // If we'd have to make the text less tall, but the width is okay, use the height.
        case bboxWidth > 1.0 && bboxHeightRatio <= 1.0:
          ratio = bboxHeightRatio
          break
      }

      // If we're using a text path, we can't use "scale" as it will start moving the item around. Instead, we can work
      // with the font size.
      let offsetLocalY = false
      if (this.followPath !== '') {
        let currentFontSize = this.obtainFontSize()
        let newFontSize = currentFontSize * ratio
        this.$el.querySelector('text').style.fontSize = Math.min(this.initialFontSize, newFontSize) + 'px'

        // Move the text if we've got a smaller font than the original one.
        offsetLocalY = newFontSize < this.initialFontSize
      } else {
        this.scale = Math.min(1.0, ratio)
        offsetLocalY = ratio < 1.0
      }

      // At this time, it works well to place the text with a Y offset with the same value as the number of characters
      // in the name.
      this.localY = offsetLocalY ? (this.textClean.length * this.heightModificationRatio) : 0
    },

    // obtainFontSize returns the calculated font size on the text element in pixels.
    obtainFontSize () {
      let el = this.$el.querySelector('text')

      // Make sure we're working with the stylesheet-defined font size when we start.
      // el.style.fontSize = 'auto'
      let fsProp = window.getComputedStyle(el, null).getPropertyValue('font-size')
      return parseFloat(fsProp.substr(0, fsProp.length - 2))
    },

    // reRender is a helper function, allowing us to easily switch between methods and timeouts.
    reRender () {
      // @todo Is there a way of knowing when the component has been properly rendered?
      setTimeout(this.fit, 100)
    }
  },

  // Re-run fitSVG every time a prop changes.
  watch: {
    textClassName () {
      this.reRender()
    },

    textClean () {
      this.reRender()
    },

    textAnchor () {
      this.reRender()
    },

    maxHeight () {
      this.reRender()
    },

    parentSVGHeight () {
      this.reRender()
    },

    width () {
      this.reRender()
    },

    containerX () {
      this.reRender()
    },

    containerY () {
      this.reRender()
    },

    x () {
      this.reRender()
    },

    y () {
      this.reRender()
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/fit-svg-text/script.js